import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSpaceStore } from '@/stores/spaceStore'
import { useAuthStore } from '@/stores/authStore'
import { useTagsStore } from '@/stores/tagsStore'
import { useCalendarStore } from '@/stores/calendarStore'
import { useDiaryStore } from '@/stores/diaryStore'
import { useEventStore } from '@/stores/eventStore'
import CreateSpaceModal from '@/modals/CreateSpaceModal'
import JoinSpaceModal from '@/modals/JoinSpaceModal'
import VaultPinModal from '@/modals/VaultPinModal'
import styles from './Sidebar.module.css'

interface Props {
  open: boolean
  onClose: () => void
}

export default function Sidebar({ open, onClose }: Props) {
  const navigate = useNavigate()
  const { activeSpace, spaces, switchSpace, loadSpaces, loadSpaceDetail } = useSpaceStore()
  const userInfo = useAuthStore(s => s.userInfo)
  const loadTags = useTagsStore(s => s.loadTags)
  const loadMonth = useCalendarStore(s => s.loadMonth)
  const clearDiary = useDiaryStore(s => s.clearDiary)
  const loadEvents = useEventStore(s => s.loadEvents)
  const [createOpen, setCreateOpen] = useState(false)
  const [joinOpen, setJoinOpen] = useState(false)
  const [vaultPinOpen, setVaultPinOpen] = useState(false)

  useEffect(() => {
    if (open) loadSpaces()
  }, [open, loadSpaces])

  const handleSelect = async (space: string, path: string) => {
    switchSpace(space)
    clearDiary()
    onClose()
    navigate(path)
    if (space.startsWith('space:')) {
      const spaceId = space.replace('space:', '')
      await loadSpaceDetail(spaceId)
    }
    await Promise.all([loadTags(), loadMonth(), loadEvents()])
  }

  return (
    <>
      <div
        className={`${styles.overlay} ${open ? '' : styles.hidden}`}
        onClick={onClose}
      />
      <div className={`${styles.sidebar} ${open ? styles.open : ''}`}>
        <div className={styles.header}>
          <span>空间</span>
          <button className={styles.headerClose} onClick={onClose}>✕</button>
        </div>
        <div className={styles.list}>
          {/* 默认空间 */}
          <button
            className={`${styles.item} ${activeSpace === 'default' ? styles.active : ''}`}
            onClick={() => handleSelect('default', '/')}
          >
            <span className={styles.itemIcon}>📅</span>
            <span className={styles.itemName}>{userInfo?.spaceName || '默认空间'}</span>
          </button>

          {/* 私密空间 */}
          {userInfo?.vaultVisible && (
            <div className={styles.itemRow}>
              <button
                className={`${styles.item} ${styles.itemFlex} ${activeSpace === 'vault' ? styles.active : ''}`}
                onClick={() => handleSelect('vault', '/vault')}
              >
                <span className={styles.itemIcon}>🔒</span>
                <span className={styles.itemName}>私密空间</span>
              </button>
              <button
                className={styles.itemSettings}
                onClick={(e) => { e.stopPropagation(); setVaultPinOpen(true) }}
                title="设置密码"
              >
                ⚙️
              </button>
            </div>
          )}

          {/* 分隔线 */}
          {spaces.length > 0 && <div className={styles.divider} />}

          {/* 情侣空间 */}
          {spaces.filter(s => s.type === 'couple').map(s => (
            <button
              key={s.id}
              className={`${styles.item} ${activeSpace === 'space:' + s.id ? styles.active : ''}`}
              onClick={() => handleSelect('space:' + s.id, '/')}
            >
              <span className={styles.itemIcon}>{s.icon || '💕'}</span>
              <div className={styles.itemInfo}>
                <span className={styles.itemName}>{s.name}</span>
                <span className={styles.itemPartner}>
                  {s.members.find(m => m.uid !== userInfo?.id)?.nickname || '等待加入'}
                </span>
              </div>
            </button>
          ))}

          {/* 团队空间 */}
          {spaces.filter(s => s.type === 'team').map(s => (
            <button
              key={s.id}
              className={`${styles.item} ${activeSpace === 'space:' + s.id ? styles.active : ''}`}
              onClick={() => handleSelect('space:' + s.id, '/')}
            >
              <span className={styles.itemIcon}>{s.icon || '👥'}</span>
              <div className={styles.itemInfo}>
                <span className={styles.itemName}>{s.name}</span>
                <span className={styles.itemCount}>{s.members.length} 人</span>
              </div>
            </button>
          ))}
        </div>
        <div className={styles.footer}>
          <button className={styles.addBtn} onClick={() => { onClose(); navigate('/space-manage') }}>📋 管理空间</button>
          <button className={styles.addBtn} onClick={() => setCreateOpen(true)}>+ 创建空间</button>
          <button className={styles.addBtn} onClick={() => setJoinOpen(true)}>🔗 加入空间</button>
        </div>
      </div>

      <CreateSpaceModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <JoinSpaceModal open={joinOpen} onClose={() => setJoinOpen(false)} />
      {vaultPinOpen && <VaultPinModal onClose={() => setVaultPinOpen(false)} />}
    </>
  )
}
